import { useEffect, useRef, useState, type DragEvent } from 'react';
import type { RaeditorStore } from '@reyka/openpolotno';
import { ImageToTemplateModal } from './ImageToTemplateModal';
import { ManageTemplatesModal } from './ManageTemplatesModal';
import { ManageElementsModal } from './ManageElementsModal';
import { SaveTemplateModal } from './SaveTemplateModal';
import { SaveTextTemplateModal } from './SaveTextTemplateModal';
import { SaveElementTemplateModal } from './SaveElementTemplateModal';
import { NewDesignModal } from './NewDesignModal';
import { ensureStoreFonts } from '../ensureStoreFonts';
import { PROGRAM_EXPORT_ORDER } from '../designSizes';
import { downloadStoreJson, importDesignFromFile } from '../importDesign';
import {
  filterTextElements,
  getSelectedElements,
} from '../selectionTemplates';

const STORAGE_KEY = 'design-studio:draft';

/** Tiny gray PNG used when canvas preview export fails */
const PLACEHOLDER_PREVIEW =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAgAAAAICAYAAADED76LAAAAFElEQVQYV2P8z8BQz0AEYBxVSF+FABJADveWkH+aAAAAAElFTkSuQmCC';

type TopBarProps = {
  store: RaeditorStore;
  status: string;
  onStatus: (message: string) => void;
};

type PageLike = {
  id: string;
  children?: unknown[];
  set?: (attrs: Record<string, unknown>) => void;
  custom?: unknown;
};

function createBlankDesign(store: RaeditorStore) {
  store.clear();
  store.addPage();
  store.history.clear();
}

/**
 * Capture a fresh template preview. Prefer active page; higher fidelity than
 * the old 0.25 ratio so overwrite thumbnails look correct.
 */
async function capturePreview(store: RaeditorStore): Promise<string> {
  const activeId =
    (store.activePage && typeof store.activePage === 'object' && 'id' in store.activePage
      ? String((store.activePage as { id?: string }).id || '')
      : '') || undefined;

  const tryToDataURL = async (pixelRatio: number): Promise<string | null> => {
    try {
      if (typeof store.toDataURL !== 'function') return null;
      const opts: { mimeType: string; pixelRatio: number; pageId?: string } = {
        mimeType: 'image/png',
        pixelRatio,
      };
      if (activeId) opts.pageId = activeId;
      const url = await store.toDataURL(opts);
      if (typeof url === 'string' && url.startsWith('data:image/')) return url;
    } catch (err) {
      console.warn('toDataURL preview failed', err);
    }
    return null;
  };

  // Prefer 0.5; fall back to 1, then without pageId
  for (const ratio of [0.5, 1]) {
    const url = await tryToDataURL(ratio);
    if (url) return url;
  }

  // Last resort: toDataURL without pageId
  try {
    if (typeof store.toDataURL === 'function') {
      const url = await store.toDataURL({ mimeType: 'image/png', pixelRatio: 0.5 });
      if (typeof url === 'string' && url.startsWith('data:image/')) return url;
    }
  } catch (err) {
    console.warn('toDataURL fallback failed', err);
  }

  // saveAsImage-style: toBlob → data URL
  try {
    if (typeof store.toBlob === 'function') {
      const blob = await store.toBlob({
        mimeType: 'image/png',
        pixelRatio: 0.5,
        ...(activeId ? { pageId: activeId } : {}),
      });
      if (blob instanceof Blob) {
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(String(reader.result || ''));
          reader.onerror = () => reject(reader.error);
          reader.readAsDataURL(blob);
        });
        if (dataUrl.startsWith('data:image/')) return dataUrl;
      }
    }
  } catch (err) {
    console.warn('toBlob preview failed', err);
  }

  return PLACEHOLDER_PREVIEW;
}

function getPages(store: RaeditorStore): PageLike[] {
  return (store.pages || []) as PageLike[];
}

function downloadDataUrl(dataUrl: string, fileName: string) {
  const a = document.createElement('a');
  a.href = dataUrl;
  a.download = fileName;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/**
 * OpenPolotno `_toPDF` filters pages with pageIds but keeps *store* order.
 * Temporarily reorder pages so export order matches PROGRAM_EXPORT_ORDER, then restore.
 */
async function withProgramPageOrder<T>(
  store: RaeditorStore,
  orderedIds: string[],
  fn: () => Promise<T>,
): Promise<T> {
  const pages = getPages(store);
  const originalIds = pages.map((p) => p.id);
  const setZ = store.setPageZIndex as ((id: string, z: number) => void) | undefined;
  if (typeof setZ === 'function') {
    orderedIds.forEach((id, i) => setZ(id, i));
  }
  try {
    return await fn();
  } finally {
    if (typeof setZ === 'function') {
      originalIds.forEach((id, i) => setZ(id, i));
    }
  }
}

export function TopBar({ store, status, onStatus }: TopBarProps) {
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [manageElementsOpen, setManageElementsOpen] = useState(false);
  const [saveTmplOpen, setSaveTmplOpen] = useState(false);
  const [saveTextOpen, setSaveTextOpen] = useState(false);
  const [saveElementOpen, setSaveElementOpen] = useState(false);
  const [newDesignOpen, setNewDesignOpen] = useState(false);
  const [programMenuOpen, setProgramMenuOpen] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [selCount, setSelCount] = useState(0);
  const [selTextCount, setSelTextCount] = useState(0);
  const importInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const sync = () => {
      const els = getSelectedElements(store);
      setSelCount(els.length);
      setSelTextCount(filterTextElements(els).length);
    };
    sync();
    // Poll selection — avoids a direct mobx dependency (store is MobX under the hood)
    const id = window.setInterval(sync, 250);
    return () => window.clearInterval(id);
  }, [store]);

  const handleExportPng = async () => {
    try {
      onStatus('Exporting PNG…');
      await store.saveAsImage({ fileName: 'design-studio.png', mimeType: 'image/png' });
      onStatus('PNG exported');
    } catch (err) {
      console.error(err);
      onStatus('PNG export failed — is the canvas ready?');
    }
  };

  const handleSave = () => {
    try {
      const json = store.toJSON();
      localStorage.setItem(STORAGE_KEY, JSON.stringify(json));
      onStatus('Saved to this browser');
    } catch (err) {
      console.error(err);
      onStatus('Save failed');
    }
  };

  const handleLoad = () => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        onStatus('No saved design found');
        return;
      }
      store.loadJSON(JSON.parse(raw));
      void ensureStoreFonts(store);
      onStatus('Loaded from this browser');
    } catch (err) {
      console.error(err);
      onStatus('Load failed — saved data may be invalid');
    }
  };

  const handleImportFile = async (file: File | null | undefined) => {
    if (!file) return;
    onStatus(`Importing ${file.name}…`);
    const result = await importDesignFromFile(store, file);
    onStatus(result.message);
  };

  const handleExportJson = () => {
    try {
      downloadStoreJson(store, 'design.json');
      onStatus('Exported design.json');
    } catch (err) {
      console.error(err);
      onStatus('Export JSON failed');
    }
  };

  const requireFourPages = (): PageLike[] | null => {
    const pages = getPages(store);
    if (pages.length < 4) {
      onStatus('Need 4 pages for program export — use New design → Program 4-page');
      return null;
    }
    return pages;
  };

  const handleExportProgramPdf = async () => {
    setProgramMenuOpen(false);
    const pages = requireFourPages();
    if (!pages) return;
    const orderedIds = PROGRAM_EXPORT_ORDER.map((i) => pages[i].id);
    try {
      onStatus('Exporting program PDF (order 1, 4, 2, 3)…');
      if (typeof store.saveAsPDF !== 'function') {
        onStatus('PDF export unavailable in this OpenPolotno build');
        return;
      }
      await withProgramPageOrder(store, orderedIds, async () => {
        await store.saveAsPDF!({
          fileName: 'program.pdf',
          pageIds: orderedIds,
        });
      });
      onStatus('Program PDF exported (pages 1 → 4 → 2 → 3)');
    } catch (err) {
      console.error(err);
      onStatus('Program PDF export failed');
    }
  };

  const handleExportProgramPng = async () => {
    setProgramMenuOpen(false);
    const pages = requireFourPages();
    if (!pages) return;
    const orderedIds = PROGRAM_EXPORT_ORDER.map((i) => pages[i].id);
    const labels = ['p1-front', 'p4-back', 'p2-inside-l', 'p3-inside-r'];
    try {
      onStatus('Exporting program PNGs (order 1, 4, 2, 3)…');
      for (let i = 0; i < orderedIds.length; i++) {
        const pageId = orderedIds[i];
        const fileName = `program-${labels[i]}.png`;
        try {
          await store.saveAsImage({
            fileName,
            mimeType: 'image/png',
            pageId,
          });
        } catch {
          const url = await store.toDataURL({ mimeType: 'image/png', pageId });
          downloadDataUrl(url, fileName);
        }
        await new Promise((r) => setTimeout(r, 350));
      }
      onStatus('Program PNGs exported (program-p1-front … p4-back, order 1→4→2→3)');
    } catch (err) {
      console.error(err);
      onStatus('Program PNG export failed');
    }
  };

  const onDropImport = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) void handleImportFile(file);
  };

  return (
    <>
      <header
        className={`top-bar${dragOver ? ' top-bar--drop' : ''}`}
        onDragEnter={(e) => {
          e.preventDefault();
          if (e.dataTransfer?.types?.includes('Files')) setDragOver(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(e) => {
          if (e.currentTarget === e.target) setDragOver(false);
        }}
        onDrop={onDropImport}
      >
        <div className="top-bar__brand">
          <span className="top-bar__mark" aria-hidden />
          <div>
            <div className="top-bar__title">Design Studio</div>
            <div className="top-bar__subtitle">Canva-style editor · OpenPolotno</div>
          </div>
        </div>

        <div className="top-bar__actions">
          <button type="button" className="btn btn--ghost" onClick={() => setNewDesignOpen(true)}>
            New design
          </button>
          <button type="button" className="btn btn--ghost" onClick={handleSave}>
            Save
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setSaveTmplOpen(true)}>
            Save as template
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setImageModalOpen(true)}>
            Image to template
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setManageOpen(true)}>
            Manage templates
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={selTextCount < 1}
            title={
              selTextCount < 1
                ? 'Select one or more text elements'
                : `Save ${selTextCount} text element(s) as Text panel preset`
            }
            onClick={() => {
              if (selTextCount < 1) {
                onStatus('Select at least one text element to save a text template');
                return;
              }
              setSaveTextOpen(true);
            }}
          >
            Save text template
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={selCount < 1}
            title={
              selCount < 1
                ? 'Select one or more canvas elements'
                : `Save ${selCount} selected element(s) as a categorized pack`
            }
            onClick={() => {
              if (selCount < 1) {
                onStatus('Select at least one element to save');
                return;
              }
              setSaveElementOpen(true);
            }}
          >
            Save element
          </button>
          <button type="button" className="btn btn--ghost" onClick={() => setManageElementsOpen(true)}>
            My elements
          </button>
          <button type="button" className="btn btn--ghost" onClick={handleLoad}>
            Load
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => importInputRef.current?.click()}
            title="Import OpenPolotno .json or .zip"
          >
            Import
          </button>
          <input
            ref={importInputRef}
            type="file"
            accept=".json,.zip,application/json,application/zip"
            style={{ display: 'none' }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              void handleImportFile(file);
            }}
          />
          <button type="button" className="btn btn--ghost" onClick={handleExportJson}>
            Export JSON
          </button>
          <button type="button" className="btn btn--primary" onClick={handleExportPng}>
            Export PNG
          </button>

          <span className="top-bar__sep" aria-hidden />

          <div className="top-bar__dropdown">
            <button
              type="button"
              className="btn btn--ghost"
              aria-expanded={programMenuOpen}
              aria-haspopup="menu"
              onClick={() => setProgramMenuOpen((o) => !o)}
            >
              Export program ▾
            </button>
            {programMenuOpen && (
              <div className="top-bar__menu" role="menu">
                <button
                  type="button"
                  className="top-bar__menu-item"
                  role="menuitem"
                  onClick={() => void handleExportProgramPdf()}
                >
                  Export PDF (1 → 4 → 2–3)
                </button>
                <button
                  type="button"
                  className="top-bar__menu-item"
                  role="menuitem"
                  onClick={() => void handleExportProgramPng()}
                >
                  Export PNGs (1 → 4 → 2–3)
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="top-bar__status" role="status" aria-live="polite">
          {status}
        </div>
      </header>

      <NewDesignModal
        open={newDesignOpen}
        onClose={() => setNewDesignOpen(false)}
        onStatus={onStatus}
        store={store}
      />
      <ImageToTemplateModal
        open={imageModalOpen}
        onClose={() => setImageModalOpen(false)}
        onStatus={onStatus}
        store={store}
      />
      <ManageTemplatesModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        onStatus={onStatus}
      />
      <SaveTemplateModal
        open={saveTmplOpen}
        onClose={() => setSaveTmplOpen(false)}
        onStatus={onStatus}
        store={store}
        capturePreview={capturePreview}
      />
      <SaveTextTemplateModal
        open={saveTextOpen}
        onClose={() => setSaveTextOpen(false)}
        onStatus={onStatus}
        store={store}
      />
      <SaveElementTemplateModal
        open={saveElementOpen}
        onClose={() => setSaveElementOpen(false)}
        onStatus={onStatus}
        store={store}
      />
      <ManageElementsModal
        open={manageElementsOpen}
        onClose={() => setManageElementsOpen(false)}
        onStatus={onStatus}
        store={store}
      />
    </>
  );
}

export { STORAGE_KEY, createBlankDesign };
